/**
 * useAppVersion Hook
 *
 * Provides robust version checking and update detection for the application.
 * This hook:
 * 1. Fetches version.json on mount to get the initial deployed version
 * 2. Polls periodically to detect new deployments
 * 3. Compares versions and triggers update notifications
 * 4. Works alongside the existing Supabase real-time notification system
 * 5. Syncs state across multiple browser tabs via storage events
 * 6. Respects offline status and network connectivity
 *
 * The version is stored in localStorage so it persists across page loads
 * and can be compared against the server version.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import {
  decideUpdate,
  fetchServedBuild,
  DEFAULT_FORCE_AFTER_MS,
  safeGetItem,
  safeRemoveItem,
  safeSetItem,
} from '@mediatechgroup/app-version';
import type { BuildInfo } from '@mediatechgroup/app-version';

interface VersionInfo {
  version: string;
  buildId: string;
  gitHash: string;
  buildTime: string;
  buildTimeMs: number;
  /** If true, this update is critical and should force reload */
  forceUpdate?: boolean;
  /** Optional message to show with force update */
  forceUpdateMessage?: string;
}

interface UseAppVersionOptions {
  /** Polling interval in milliseconds (default: 5 minutes) */
  pollInterval?: number;
  /** Whether to enable polling (default: true) */
  enablePolling?: boolean;
  /** Callback when a new version is detected */
  onNewVersionDetected?: (newVersion: VersionInfo, currentVersion: VersionInfo | null) => void;
  /** Callback when a force update is required (critical security patch) */
  onForceUpdateRequired?: (newVersion: VersionInfo, message?: string) => void;
  /** Dismissal cooldown in milliseconds (default: 30 minutes) */
  dismissalCooldownMs?: number;
  /** Rate limit interval in milliseconds (default: 60 seconds) */
  rateLimitIntervalMs?: number;
  /** Snooze duration in milliseconds (default: 1 hour) */
  snoozeDurationMs?: number;
  /**
   * How far a running bundle may fall behind the deployed one before the
   * update stops being declinable (default: 7 days). 0 disables the backstop.
   *
   * This is the half that covers a tab nobody thought to flag. Every update
   * was dismissible and "Remind me later" simply reset the timer, so a tab
   * left open could run stale code indefinitely — on 2026-09-15 one had been
   * doing so since the previous day, calling an endpoint no current source
   * file references and replaying a token the server had revoked.
   *
   * A week is deliberately generous: long enough that nobody is pulled out of
   * a half-finished form by a routine deploy, short enough that a bundle
   * cannot outlive the API it was written against.
   */
  forceAfterMs?: number;
}

interface UseAppVersionReturn {
  /** Current loaded version info */
  currentVersion: VersionInfo | null;
  /** Latest version available from server */
  latestVersion: VersionInfo | null;
  /** Whether a new version is available */
  hasUpdate: boolean;
  /** Whether a force update is required (critical) */
  isForceUpdate: boolean;
  /** Force update message if any */
  forceUpdateMessage: string | null;
  /** Whether we're currently checking for updates */
  isChecking: boolean;
  /** Any error that occurred during version check */
  error: string | null;
  /** Whether the browser is currently offline */
  isOffline: boolean;
  /** Manually trigger a version check (force=true bypasses rate limiting) */
  checkForUpdate: (force?: boolean) => Promise<boolean>;
  /** Reload the application to get the new version */
  reloadToUpdate: () => void;
  /** Dismiss the update notification (until next check) */
  dismissUpdate: () => void;
  /** Snooze the notification for a configurable duration (default 1 hour) */
  snoozeUpdate: () => void;
}

const VERSION_STORAGE_KEY = 'app_version_info';
const UPDATE_DISMISSED_KEY = 'app_update_dismissed_version';
const LAST_CHECK_TIME_KEY = 'app_version_last_check';
const DISMISSAL_COOLDOWN_KEY = 'app_update_dismissal_time';
const JUST_RELOADED_FOR_UPDATE_KEY = 'app_just_reloaded_for_update';
const SNOOZE_UNTIL_KEY = 'app_update_snooze_until';

// Default: Minimum time between version checks (60 seconds)
const DEFAULT_RATE_LIMIT_MS = 60 * 1000;
// Default: After dismissing, don't show notification for at least 30 minutes
const DEFAULT_DISMISSAL_COOLDOWN_MS = 30 * 60 * 1000;
// Default: Snooze duration (1 hour)
const DEFAULT_SNOOZE_DURATION_MS = 60 * 60 * 1000;
// The age limit's default is the package's (7 days): the same week this hook
// always used, kept in one place.

/**
 * The key a dismissal is stored and compared under: the commit alone. The
 * Supabase-triggered notification path (App.tsx) writes the same key from
 * appUpdateRequestedGitHash, so the two must agree; the package's default
 * (hash and build time) would not.
 */
const keyOfBuild = (build: BuildInfo): string => build.gitHash;

export const useAppVersion = (options: UseAppVersionOptions = {}): UseAppVersionReturn => {
  const {
    pollInterval = 5 * 60 * 1000, // 5 minutes default
    enablePolling = true,
    onNewVersionDetected,
    onForceUpdateRequired,
    dismissalCooldownMs = DEFAULT_DISMISSAL_COOLDOWN_MS,
    rateLimitIntervalMs = DEFAULT_RATE_LIMIT_MS,
    snoozeDurationMs = DEFAULT_SNOOZE_DURATION_MS,
    forceAfterMs = DEFAULT_FORCE_AFTER_MS,
  } = options;

  const [currentVersion, setCurrentVersion] = useState<VersionInfo | null>(null);
  const [latestVersion, setLatestVersion] = useState<VersionInfo | null>(null);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isForceUpdate, setIsForceUpdate] = useState(false);
  const [forceUpdateMessage, setForceUpdateMessage] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const onNewVersionDetectedRef = useRef(onNewVersionDetected);
  const onForceUpdateRequiredRef = useRef(onForceUpdateRequired);
  const initialCheckDoneRef = useRef(false);
  const isCheckingRef = useRef(false); // Prevent concurrent checks
  // The deployed version this tab saw on its first check (its load-time baseline).
  const tabBaselineRef = useRef<VersionInfo | null>(null);
  const latestVersionRef = useRef<VersionInfo | null>(null); // Avoid stale closure
  const currentVersionRef = useRef<VersionInfo | null>(null); // Avoid stale closure

  // Keep refs updated to avoid stale closures
  useEffect(() => {
    onNewVersionDetectedRef.current = onNewVersionDetected;
  }, [onNewVersionDetected]);

  useEffect(() => {
    onForceUpdateRequiredRef.current = onForceUpdateRequired;
  }, [onForceUpdateRequired]);

  useEffect(() => {
    latestVersionRef.current = latestVersion;
  }, [latestVersion]);

  useEffect(() => {
    currentVersionRef.current = currentVersion;
  }, [currentVersion]);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
    };
    const handleOffline = () => {
      setIsOffline(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  /**
   * Check if we're within the cooldown period after dismissal
   */
  const isInDismissalCooldown = useCallback((): boolean => {
    try {
      const dismissalTime = safeGetItem(DISMISSAL_COOLDOWN_KEY);
      if (!dismissalTime) return false;
      const dismissalTimestamp = parseInt(dismissalTime, 10);
      // If localStorage is corrupted and parseInt returns NaN, don't enforce cooldown
      if (isNaN(dismissalTimestamp)) return false;
      const timeSinceDismissal = Date.now() - dismissalTimestamp;
      return timeSinceDismissal < dismissalCooldownMs;
    } catch {
      return false;
    }
  }, [dismissalCooldownMs]);

  /**
   * Check if enough time has passed since last check (rate limiting)
   */
  const canPerformCheck = useCallback((): boolean => {
    try {
      const lastCheck = safeGetItem(LAST_CHECK_TIME_KEY);
      if (!lastCheck) return true;
      const lastCheckTime = parseInt(lastCheck, 10);
      // If localStorage is corrupted and parseInt returns NaN, allow the check
      if (isNaN(lastCheckTime)) return true;
      const timeSinceLastCheck = Date.now() - lastCheckTime;
      return timeSinceLastCheck >= rateLimitIntervalMs;
    } catch {
      return true;
    }
  }, [rateLimitIntervalMs]);

  /**
   * Record the time of a version check
   */
  const recordCheckTime = useCallback(() => {
    try {
      safeSetItem(LAST_CHECK_TIME_KEY, Date.now().toString());
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  /**
   * Fetch version.json with retry and exponential backoff.
   *
   * @mediatechgroup/app-version's fetchServedBuild (PM a2640c12), ported from
   * this hook: a cache-busted, no-store GET with a 10 s timeout per attempt,
   * up to `maxRetries` attempts 2 s, 4 s, 8 s apart, null on failure and
   * never a throw. One difference, deliberate: a body without a numeric
   * buildTimeMs reads as no manifest (null), where this hook accepted any
   * object with a gitHash; generate-version.js has always written both, and
   * the age limit cannot be measured without it. The pre-adoption hook's
   * answers are pinned in src/hooks/__tests__/useAppVersion.pinned.test.ts.
   *
   * Version check failures are transient (network issues, CDN blips) and not
   * actionable: nothing is reported to Sentry.
   */
  const fetchVersionWithRetry = useCallback(async (maxRetries: number = 3): Promise<VersionInfo | null> => {
    const served = await fetchServedBuild({ attempts: maxRetries });
    // The manifest is the file as served: generate-version.js writes every
    // VersionInfo field, so the package's BuildInfo view of it is the same object.
    return served ? (served as unknown as VersionInfo) : null;
  }, []);

  /**
   * Check if we're within a snooze period
   */
  const isInSnooze = useCallback((): boolean => {
    try {
      const snoozeUntil = safeGetItem(SNOOZE_UNTIL_KEY);
      if (!snoozeUntil) return false;
      const snoozeUntilTime = parseInt(snoozeUntil, 10);
      if (isNaN(snoozeUntilTime)) return false;
      return Date.now() < snoozeUntilTime;
    } catch {
      return false;
    }
  }, []);

  /**
   * Load stored version from localStorage with corruption recovery
   */
  const loadStoredVersion = useCallback((): VersionInfo | null => {
    try {
      const stored = safeGetItem(VERSION_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Validate the parsed data has the required structure
        if (parsed && typeof parsed.gitHash === 'string') {
          return parsed as VersionInfo;
        }
        // Data is corrupted - remove it
        safeRemoveItem(VERSION_STORAGE_KEY);
        return null;
      }
    } catch (err) {
      // Only unparseable JSON reaches here: the read goes through safeGetItem,
      // so a browser that DENIES storage (an embed iframe with third-party
      // storage blocked, Sentry CLUB-CENTRAL-TENANT-5A) reads null instead of
      // throwing and is not reported as corruption.
      Sentry.captureMessage('localStorage version data corrupted', {
        level: 'warning',
        tags: { component: 'useAppVersion', operation: 'loadStoredVersion' },
        extra: { error: err instanceof Error ? err.message : String(err) },
      });
      // Clear corrupted data
      try {
        safeRemoveItem(VERSION_STORAGE_KEY);
      } catch {
        // Ignore cleanup errors
      }
    }
    return null;
  }, []);

  /**
   * Save version to localStorage
   */
  const saveVersion = useCallback((version: VersionInfo) => {
    try {
      safeSetItem(VERSION_STORAGE_KEY, JSON.stringify(version));
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  /**
   * The gitHash the person last declined, if any (the key dismissals are
   * stored under; see keyOfBuild).
   */
  const readDismissedKey = useCallback((): string | null => {
    try {
      return safeGetItem(UPDATE_DISMISSED_KEY);
    } catch {
      return null;
    }
  }, []);

  /**
   * Is the served build an update on the one this tab ACCEPTED (its current
   * or stored version), and was it declined? The package's policy
   * (@mediatechgroup/app-version decideUpdate, PM a2640c12) compared by
   * gitHash alone: the timestamp-based version and random buildId change on
   * every build, even without code changes, and used to cause false
   * positives. The age limit is off here: the age is decided once, in
   * checkForUpdate, from what the tab LOADED, because storage can be weeks
   * old for a page that loaded the latest bundle a second ago (2026-09-24).
   */
  const decideServedAgainst = useCallback((accepted: VersionInfo, served: VersionInfo) => {
    // Kept local (not a decideUpdate option): a build without a commit cannot
    // be compared, so it is "the same", never a false positive. Pre-gitHash
    // storage is discarded by loadStoredVersion and generate-version.js always
    // writes a hash, so only an empty string reaches here.
    if (!accepted.gitHash || !served.gitHash) return { kind: 'current' as const };
    return decideUpdate({
      running: accepted,
      served,
      dismissedKey: readDismissedKey(),
      compareBy: 'hash',
      keyOf: keyOfBuild,
      forceAfterMs: 0,
    });
  }, [readDismissedKey]);

  /**
   * Main version check function with rate limiting and concurrency protection
   */
  const checkForUpdate = useCallback(async (force: boolean = false): Promise<boolean> => {
    // Prevent concurrent checks
    if (isCheckingRef.current) {
      return false;
    }

    // Skip check if offline
    if (!navigator.onLine) {
      return false;
    }

    // Check if user just reloaded for an update - if so, update stored version silently
    let justReloadedForUpdate = false;
    try {
      justReloadedForUpdate = safeGetItem(JUST_RELOADED_FOR_UPDATE_KEY) === 'true';
      if (justReloadedForUpdate) {
        safeRemoveItem(JUST_RELOADED_FOR_UPDATE_KEY);
      }
    } catch {
      // localStorage might be disabled (private browsing), continue without the flag
    }

    // Rate limiting - don't check too frequently (unless forced or just reloaded)
    if (!force && !justReloadedForUpdate && !canPerformCheck()) {
      return false;
    }

    // ⚠️ Dismissal and snooze are checked AFTER the fetch now, not here.
    //
    // They used to return BEFORE it, which meant a declined update stopped the
    // app from even LOOKING at version.json. So the two things that are
    // supposed to override a dismissal — a flagged release, and a bundle that
    // has fallen too far behind — could never be discovered, because
    // discovering them requires the fetch that the dismissal had already
    // skipped. Declining once was enough to go blind to everything after it,
    // which is how a tab ends up running week-old code.
    //
    // Rate limiting stays here: it is about request volume, not about what the
    // user chose, and it does not hide anything from us permanently.

    isCheckingRef.current = true;
    setIsChecking(true);
    setError(null);
    recordCheckTime();

    try {
      // Use retry with exponential backoff for better reliability
      const serverVersion = await fetchVersionWithRetry(3);

      if (!serverVersion) {
        setError('Could not fetch version information');
        return false;
      }

      setLatestVersion(serverVersion);

      // Two ways an update stops being declinable (the package's policy,
      // @mediatechgroup/app-version decideUpdate, ported from this hook):
      //
      //   1. the deploy was FLAGGED (generate-version.js --force-update), or
      //   2. the bundle this tab is running has fallen more than forceAfterMs
      //      behind the served one.
      //
      // (2) is the backstop for the tab nobody flagged: a tab open since last
      // week forces even though every deploy since was routine. Both clear
      // snooze and dismissal below, because a declined prompt is exactly how a
      // tab gets this stale in the first place: "Remind me later" just reset
      // the timer, forever.
      //
      // `running` is what THIS TAB loaded — the build it saw on its first
      // check — never storage. Storage is written only on the narrow "reloaded
      // for an update" path, so it can be weeks old for a page that loaded the
      // latest bundle a second ago. Result (2026-09-24): a red "reload" banner
      // that no number of reloads could clear, for anyone who had not taken an
      // amber update in 7 days. A fresh page load IS running what is deployed;
      // only a tab left OPEN past forceAfterMs is stale. The same holds for a
      // flagged release: a tab that loaded the flagged build is current, and
      // is not told to reload onto the build it is already running (the
      // pre-adoption hook did, on every check, which is the loop acceptance 1 of
      // PM a2640c12 rules out). `compareBy: 'hash'`: a same-commit rebuild (a
      // "clear cache and deploy") is the same code, not an update.
      if (!tabBaselineRef.current) tabBaselineRef.current = serverVersion;
      const forced = decideUpdate({
        running: tabBaselineRef.current,
        served: serverVersion,
        compareBy: 'hash',
        keyOf: keyOfBuild,
        forceAfterMs,
      });

      if (forced.kind === 'force') {
        setIsForceUpdate(true);
        setForceUpdateMessage(forced.message);
        setHasUpdate(true);
        // Clear any snooze or dismissal
        try {
          safeRemoveItem(SNOOZE_UNTIL_KEY);
          safeRemoveItem(UPDATE_DISMISSED_KEY);
          safeRemoveItem(DISMISSAL_COOLDOWN_KEY);
        } catch {
          // Ignore localStorage errors
        }
        onForceUpdateRequiredRef.current?.(serverVersion, serverVersion.forceUpdateMessage);
        return true;
      }

      // Not forced, so the user's choice stands: stay quiet through a snooze or
      // a dismissal cooldown. Moved below the force decision rather than above
      // the fetch, so declining silences the NOTIFICATION without also
      // blinding the check that would have overridden it.
      if (!justReloadedForUpdate && isInDismissalCooldown()) {
        return false;
      }
      if (!force && !justReloadedForUpdate && isInSnooze()) {
        return false;
      }

      // If user just reloaded for update, update stored version silently
      // The user intentionally clicked "Refresh" so they now have the new version
      if (justReloadedForUpdate) {
        setCurrentVersion(serverVersion);
        setIsForceUpdate(false);
        setForceUpdateMessage(null);
        saveVersion(serverVersion);
        return false;
      }

      // Use ref for current version to avoid stale closure
      const currentVer = currentVersionRef.current;

      // If we don't have a current version, this is the initial load
      if (!currentVer) {
        const storedVersion = loadStoredVersion();

        if (!storedVersion) {
          // First time load - save this as our baseline
          setCurrentVersion(serverVersion);
          saveVersion(serverVersion);
          return false;
        }

        // We have a stored version - compare with server
        setCurrentVersion(storedVersion);

        const stored = decideServedAgainst(storedVersion, serverVersion);
        if (stored.kind === 'current') {
          // Stored version matches server (user already has latest):
          // update it to ensure it's current
          saveVersion(serverVersion);
          return false;
        }
        if (stored.kind === 'dismissed') {
          // Update stored version since user dismissed this version
          saveVersion(serverVersion);
          setCurrentVersion(serverVersion);
          return false;
        }
        // 'prompt', or 'force' for a flagged build this tab already runs (not
        // forced above) that storage has not caught up with: an update on
        // what was stored, declinable as any other.
        setHasUpdate(true);
        onNewVersionDetectedRef.current?.(serverVersion, storedVersion);
        return true;
      }

      // We have a current version - compare with server
      const current = decideServedAgainst(currentVer, serverVersion);
      if (current.kind === 'prompt' || current.kind === 'force') {
        setHasUpdate(true);
        onNewVersionDetectedRef.current?.(serverVersion, currentVer);
        return true;
      }

      return false;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      // Report to Sentry for monitoring
      Sentry.captureException(err, {
        tags: { component: 'useAppVersion', operation: 'checkForUpdate' },
      });
      return false;
    } finally {
      isCheckingRef.current = false;
      setIsChecking(false);
    }
  }, [fetchVersionWithRetry, loadStoredVersion, saveVersion, decideServedAgainst, canPerformCheck, isInDismissalCooldown, isInSnooze, recordCheckTime, forceAfterMs]);

  /**
   * Reload the page to get the new version
   *
   * Sets a flag indicating the user intentionally reloaded for an update.
   * On the next load, checkForUpdate will see this flag and update the
   * stored version without showing a notification.
   */
  const reloadToUpdate = useCallback(() => {
    try {
      // Set flag to indicate intentional reload for update
      // This prevents showing a notification immediately after reload
      safeSetItem(JUST_RELOADED_FOR_UPDATE_KEY, 'true');

      // Clear notification-related flags but NOT the stored version
      safeRemoveItem(UPDATE_DISMISSED_KEY);
      safeRemoveItem(LAST_CHECK_TIME_KEY);
      safeRemoveItem(DISMISSAL_COOLDOWN_KEY);
      safeRemoveItem('appUpdateRequested');
      safeRemoveItem('appUpdateDismissed');
      safeRemoveItem('appUpdateRequestedGitHash');
      safeRemoveItem('appUpdateRequestedVersionData');
    } catch (err) {
      // localStorage might be full or unavailable, proceed with reload anyway
    }

    // Force a hard reload bypassing cache
    window.location.reload();
  }, []);

  /**
   * Dismiss the update notification
   * Saves the gitHash to prevent re-showing for the same version
   * Also updates the stored version to prevent future false-positive detections
   *
   * IMPORTANT: This function should NOT overwrite an already-set dismissed gitHash.
   * The App.tsx dismiss handler may have already set the correct gitHash from
   * appUpdateRequestedGitHash (for Supabase-triggered notifications).
   */
  const dismissUpdate = useCallback(() => {
    try {
      // Get the gitHash that should be dismissed
      // Priority: 1) Already set dismissed hash, 2) appUpdateRequestedGitHash, 3) latestVersionRef
      const alreadyDismissedHash = safeGetItem(UPDATE_DISMISSED_KEY);
      const requestedGitHash = safeGetItem('appUpdateRequestedGitHash');
      const latestVer = latestVersionRef.current;

      // Determine which gitHash to use for dismissal
      let hashToDismiss: string | null = null;

      if (requestedGitHash) {
        // Use the gitHash from the notification that was shown
        hashToDismiss = requestedGitHash;
      } else if (latestVer?.gitHash) {
        // Fall back to hook's latestVersion
        hashToDismiss = latestVer.gitHash;
      }

      // Only update dismissed hash if we have a new one and it's different
      if (hashToDismiss && hashToDismiss !== alreadyDismissedHash) {
        safeSetItem(UPDATE_DISMISSED_KEY, hashToDismiss);
        // Set the cooldown timestamp - prevents notifications for 30 minutes
        safeSetItem(DISMISSAL_COOLDOWN_KEY, Date.now().toString());
      } else if (alreadyDismissedHash) {
        // Still set cooldown even if hash was already set (user is actively dismissing)
        safeSetItem(DISMISSAL_COOLDOWN_KEY, Date.now().toString());
      }

      // Update stored version - try latestVersion first, fall back to Supabase-stored data
      if (latestVer) {
        saveVersion(latestVer);
        setCurrentVersion(latestVer);
      } else {
        // Fallback: use version data stored by Supabase handler
        // This handles the case where Supabase triggers notification before hook polls
        try {
          const storedVersionData = safeGetItem('appUpdateRequestedVersionData');
          if (storedVersionData) {
            const versionData = JSON.parse(storedVersionData);
            saveVersion(versionData);
            setCurrentVersion(versionData);
          }
        } catch {
          // Ignore parse errors
        }
      }

      // Clean up notification trigger flags
      safeRemoveItem('appUpdateRequested');
      safeRemoveItem('appUpdateRequestedGitHash');
      safeRemoveItem('appUpdateRequestedVersionData');
    } catch (err) {
      // localStorage might be full or unavailable
      Sentry.captureException(err, {
        level: 'warning',
        tags: { component: 'useAppVersion', operation: 'dismissUpdate' },
      });
    }

    // Clear the update flag - always do this even if localStorage fails
    setHasUpdate(false);
    setIsForceUpdate(false);
    setForceUpdateMessage(null);
  }, [saveVersion]);

  /**
   * Snooze the update notification for a configurable duration
   * User can choose "Remind me later" and the notification won't appear
   * until the snooze period expires
   *
   * IMPORTANT: This function must properly update stored version and set
   * dismissal state, in case toast.dismiss() doesn't trigger onClose reliably.
   */
  const snoozeUpdate = useCallback(() => {
    try {
      const snoozeUntil = Date.now() + snoozeDurationMs;
      safeSetItem(SNOOZE_UNTIL_KEY, snoozeUntil.toString());

      // Get the gitHash that was being shown
      const requestedGitHash = safeGetItem('appUpdateRequestedGitHash');
      const latestVer = latestVersionRef.current;

      // Set the dismissal state (in case onClose doesn't fire)
      // This ensures after snooze expires, the version is still marked as dismissed
      if (requestedGitHash) {
        safeSetItem(UPDATE_DISMISSED_KEY, requestedGitHash);
      } else if (latestVer?.gitHash) {
        safeSetItem(UPDATE_DISMISSED_KEY, latestVer.gitHash);
      }

      // Also set dismissal cooldown as a backup (30 minutes)
      safeSetItem(DISMISSAL_COOLDOWN_KEY, Date.now().toString());

      // Update stored version to match server version
      // This prevents false-positive update detection after snooze
      if (latestVer) {
        saveVersion(latestVer);
        setCurrentVersion(latestVer);
      } else {
        // Fallback: use version data stored by notification handler
        const storedVersionData = safeGetItem('appUpdateRequestedVersionData');
        if (storedVersionData) {
          try {
            const versionData = JSON.parse(storedVersionData);
            saveVersion(versionData);
            setCurrentVersion(versionData);
          } catch {
            // Ignore parse errors
          }
        }
      }

      // Clear the notification trigger flags
      safeRemoveItem('appUpdateRequested');
      safeRemoveItem('appUpdateRequestedGitHash');
      safeRemoveItem('appUpdateRequestedVersionData');
    } catch (err) {
      Sentry.captureException(err, {
        level: 'warning',
        tags: { component: 'useAppVersion', operation: 'snoozeUpdate' },
      });
    }

    // Clear the update flag
    setHasUpdate(false);
    setIsForceUpdate(false);
    setForceUpdateMessage(null);
  }, [snoozeDurationMs, saveVersion]);

  // Keep a stable ref to checkForUpdate to avoid recreating intervals
  const checkForUpdateRef = useRef(checkForUpdate);
  useEffect(() => {
    checkForUpdateRef.current = checkForUpdate;
  }, [checkForUpdate]);

  // Initial version check on mount - use force=true to bypass rate limiting on first load
  useEffect(() => {
    if (!initialCheckDoneRef.current) {
      initialCheckDoneRef.current = true;
      // Use the ref to call, but pass force=true for initial check
      checkForUpdateRef.current(true);
    }
  }, []); // Empty deps - only run once on mount

  // Set up polling - uses ref to avoid recreating interval when checkForUpdate changes
  useEffect(() => {
    if (!enablePolling) return;

    // Clear any existing interval
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }

    // Set up new polling interval using the ref
    pollIntervalRef.current = setInterval(() => {
      checkForUpdateRef.current();
    }, pollInterval);

    // Cleanup on unmount
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [enablePolling, pollInterval]); // Removed checkForUpdate from deps

  // Check for updates when tab becomes visible (user returns to app)
  // Rate limiting is handled inside checkForUpdate
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Delay check to allow page to stabilize
        // Rate limiting inside checkForUpdate will prevent too-frequent checks
        setTimeout(() => {
          checkForUpdateRef.current();
        }, 2000); // Increased delay to 2 seconds
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []); // Empty deps - handler uses ref

  // Multi-tab synchronization: Listen for storage changes from other tabs
  // When another tab dismisses an update or updates the stored version, sync state
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      // Another tab updated the stored version
      if (e.key === VERSION_STORAGE_KEY && e.newValue) {
        try {
          const newVersion = JSON.parse(e.newValue) as VersionInfo;
          setCurrentVersion(newVersion);
          // If we have an update notification and the stored version now matches,
          // clear the update flag
          if (hasUpdate && latestVersionRef.current?.gitHash === newVersion.gitHash) {
            setHasUpdate(false);
          }
        } catch {
          // Ignore parse errors
        }
      }

      // Another tab dismissed an update
      if (e.key === UPDATE_DISMISSED_KEY && e.newValue) {
        const dismissedHash = e.newValue;
        // If we're showing notification for this version, clear it
        if (latestVersionRef.current?.gitHash === dismissedHash) {
          setHasUpdate(false);
        }
      }

      // Another tab set dismissal cooldown
      if (e.key === DISMISSAL_COOLDOWN_KEY && e.newValue) {
        // If we have an update showing, clear it (respect the cooldown)
        setHasUpdate(false);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [hasUpdate]); // Include hasUpdate so we can check against it

  // Version info tracking on mount
  useEffect(() => {
    if (currentVersion) {
      // Version info available for debugging via React DevTools
    }
  }, [currentVersion]);

  return {
    currentVersion,
    latestVersion,
    hasUpdate,
    isForceUpdate,
    forceUpdateMessage,
    isChecking,
    error,
    isOffline,
    checkForUpdate,
    reloadToUpdate,
    dismissUpdate,
    snoozeUpdate,
  };
};

export default useAppVersion;
